import { httpApi } from '@app/api/http.api';
import { IChannel } from '@app/interfaces/channel';
import { IChat } from '@app/interfaces/chat';
import {
  IDealRoom,
  IDealRoomChatChannel,
  IDealRoomChatComment,
  IDealRoomComment,
  IDealRoomDeleteComment,
  IDealRoomDeleteCommentExternal,
  IDealRoomEditComment,
  IDealRoomEditCommentExternal,
  IDealRoomExternal,
  IDealRoomSectionUpdate,
  IDealRoomShare,
  IDealRoomSummary,
  IDealRoomWhatsApp,
} from '@app/interfaces/deal-room';
import { IDealRoomPeople } from '@app/interfaces/deal-room-people';
import { SearchQueryResponse } from '@app/interfaces/interfaces';
import { QueryRequest } from '@app/store/slices/userSlice';
import { handleHttpRequest } from './common.api';
import { IMouseflowScore } from '@app/interfaces/mouseflow-score';

const baseSlug = '/deal-rooms';

export const readDealRoom = async (id: string): Promise<void> => {
  try {
    await httpApi.post<void>(`${baseSlug}/read/${id}`, {});
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const getUnreadDealRoom = async (): Promise<{ unread: number }> => {
  try {
    const { data } = await httpApi.get<{ unread: number }>(`${baseSlug}/unread`);
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const updateDealRoom = async (id: string, payload: Partial<IDealRoom>): Promise<IDealRoom> => {
  return handleHttpRequest<IDealRoom, Partial<IDealRoom>>('patch', `${baseSlug}/${id}`, payload);
};

export const deleteDealRoom = async (id: string): Promise<undefined> => {
  try {
    const { data } = await httpApi.delete<undefined>(`${baseSlug}/${id}`);
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const getDealRoomListWithPagination = async (queryPayload: QueryRequest): Promise<SearchQueryResponse> => {
  try {
    const { data } = await httpApi.get<SearchQueryResponse>(
      `${baseSlug}/queryDealRoomsWithPagination?${new URLSearchParams(
        queryPayload as Record<string, string>,
      ).toString()}`,
    );
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const getDealRoom = async (id: string): Promise<IDealRoom> => {
  try {
    const { data } = await httpApi.get<IDealRoom>(`${baseSlug}/${id}`);
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const getDealRoomExternalByKey = async (
  id: string,
  key: string,
  payload: Partial<IDealRoomPeople>,
): Promise<IDealRoomShare> => {
  return handleHttpRequest<IDealRoomShare, Partial<IDealRoomPeople>>(
    'post',
    `${baseSlug}/external/${id}/${key}`,
    payload,
  );
};

export const getDealRoomByKey = async (
  id: string,
  key: string,
  payload: Partial<IDealRoomPeople>,
): Promise<IDealRoomShare> => {
  return handleHttpRequest<IDealRoomShare, Partial<IDealRoomPeople>>('post', `${baseSlug}/${id}/${key}`, payload);
};

export const getDealRoomExternalDetails = async (
  id: string,
  key: string,
  payload: Partial<IDealRoomPeople>,
): Promise<IDealRoomShare> => {
  return handleHttpRequest<IDealRoomShare, Partial<IDealRoomPeople>>(
    'post',
    `${baseSlug}/externalDetails/${id}/${key}`,
    payload,
  );
};

export const getDealRoomDetails = async (id: string, key: string): Promise<IDealRoomShare> => {
  return handleHttpRequest<IDealRoomShare, Partial<IDealRoomPeople>>('post', `${baseSlug}/details/${id}/${key}`, {});
};

export const getDealRoomOnlinePeopleExternal = async (
  id: string,
  query: IDealRoomExternal,
): Promise<IDealRoomPeople[]> => {
  try {
    const { data } = await httpApi.get<IDealRoomPeople[]>(`${baseSlug}/onlinePeopleExternal/${id}`, {
      params: query,
    });
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const getDealRoomOnlinePeople = async (id: string): Promise<IDealRoomPeople[]> => {
  try {
    const { data } = await httpApi.get<IDealRoomPeople[]>(`${baseSlug}/onlinePeople/${id}`);
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const getDealRoomExternalComments = async (
  dealRoomId: string,
  channelId: string,
  payload: IDealRoomChatComment,
): Promise<IChat[]> => {
  return handleHttpRequest<IChat[], IDealRoomChatComment>(
    'post',
    `${baseSlug}/externalComments/${dealRoomId}/${channelId}`,
    payload,
  );
};

export const getDealRoomComments = async (dealRoomId: string, channelId: string): Promise<IChat[]> => {
  return handleHttpRequest<IChat[], Record<string, string>>(
    'post',
    `${baseSlug}/comments/${dealRoomId}/${channelId}`,
    {},
  );
};

export const addDealRoomComment = async (
  dealRoomId: string,
  channelId: string,
  payload: IDealRoomComment,
): Promise<IChat> => {
  return handleHttpRequest<IChat, IDealRoomComment>('post', `${baseSlug}/comment/${dealRoomId}/${channelId}`, payload);
};

export const editDealRoomCommentExternal = async (
  id: string,
  chatId: string,
  payload: IDealRoomEditCommentExternal,
): Promise<undefined> => {
  return handleHttpRequest<undefined, IDealRoomEditCommentExternal>(
    'post',
    `${baseSlug}/editCommentExternal/${id}/${chatId}`,
    payload,
  );
};

export const editDealRoomComment = async (
  id: string,
  chatId: string,
  payload: IDealRoomEditComment,
): Promise<undefined> => {
  return handleHttpRequest<undefined, IDealRoomEditComment>('post', `${baseSlug}/editComment/${id}/${chatId}`, payload);
};

export const deleteDealRoomCommentExternal = async (
  id: string,
  chatId: string,
  payload: IDealRoomDeleteCommentExternal,
): Promise<undefined> => {
  return handleHttpRequest<undefined, IDealRoomDeleteCommentExternal>(
    'post',
    `${baseSlug}/deleteCommentExternal/${id}/${chatId}`,
    payload,
  );
};

export const deleteDealRoomComment = async (
  id: string,
  chatId: string,
  payload: IDealRoomDeleteComment,
): Promise<undefined> => {
  return handleHttpRequest<undefined, IDealRoomDeleteComment>(
    'post',
    `${baseSlug}/deleteComment/${id}/${chatId}`,
    payload,
  );
};

export const getDealRoomChannel = async (id: string): Promise<IChannel> => {
  try {
    const { data } = await httpApi.get<IChannel>(`${baseSlug}/channel/${id}`);
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const getDealRoomSummary = async (id: string): Promise<IDealRoomSummary> => {
  try {
    const { data } = await httpApi.get<IDealRoomSummary>(`${baseSlug}/summary/${id}`);
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const updateDealRoomSections = async (id: string, payload: IDealRoomSectionUpdate): Promise<IDealRoom> => {
  return handleHttpRequest<IDealRoom, IDealRoomSectionUpdate>('post', `${baseSlug}/sections/${id}`, payload);
};

export const getDealRoomPeopleChannel = async (id: string): Promise<IChannel> => {
  try {
    const { data } = await httpApi.get<IChannel>(`${baseSlug}/peopleChannel/${id}`);
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const getDealRoomChatChannel = async (id: string, dealRoomPeople: string): Promise<IDealRoomChatChannel[]> => {
  try {
    const { data } = await httpApi.get<IDealRoomChatChannel[]>(
      `${baseSlug}/externalChatChannels/${id}/${dealRoomPeople}`,
    );
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const getDealRoomChatChannelByUser = async (id: string): Promise<IDealRoomChatChannel[]> => {
  try {
    const { data } = await httpApi.get<IDealRoomChatChannel[]>(`${baseSlug}/chatChannels/${id}`);
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const getDealRoomShortUrl = async (
  dealRoomId: string,
): Promise<{
  shortUrl: string;
}> => {
  try {
    const { data } = await httpApi.get<{ shortUrl: string }>(`${baseSlug}/shortUrl/${dealRoomId}`);
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const getChatInfo = async (id: string): Promise<IDealRoomShare> => {
  return handleHttpRequest<IDealRoomShare, undefined>('get', `${baseSlug}/chatInfo/${id}`);
};

export const sendWhatsApp = async (dealRoomId: string, members: IDealRoomWhatsApp): Promise<undefined> => {
  return handleHttpRequest<undefined, IDealRoomWhatsApp>('post', `${baseSlug}/sendWhatsApp/${dealRoomId}`, members);
};

export const sendSms = async (dealRoomId: string, members: IDealRoomWhatsApp): Promise<undefined> => {
  return handleHttpRequest<undefined, IDealRoomWhatsApp>('post', `${baseSlug}/sendSms/${dealRoomId}`, members);
};

export const getClientReview = async (dealRoomId: string): Promise<{ remarks: string }> => {
  return handleHttpRequest<{ remarks: string }, undefined>('get', `${baseSlug}/clientReview/${dealRoomId}`);
};

export const getMouseflowDetails = async (dealRoomId: string): Promise<IMouseflowScore | null> => {
  return handleHttpRequest<IMouseflowScore | null, undefined>('get', `${baseSlug}/mouseflowDetails/${dealRoomId}`);
};
